import type { PositionSizeRule, BacktestReplenishmentRule } from '@einhaus/project314-types';

export interface PortfolioBacktestDuplicateSource {
    settings: {
        replenishmentRules?: BacktestReplenishmentRule[];
        positionSizeRules?: PositionSizeRule[];
    };
    strategiesSubscribed: readonly {
        id: number;
        tradingProfileId?: number | null;
        tradingProfile?: { id: number; name: string } | null | void;
        optionBuyingStrategyIds?: number[];
        exitCandidateIds?: number[];
        portfolioBacktestPositionSizeRules?: PositionSizeRule[];
    }[];
}

export interface PortfolioBacktestDuplicateLiveStrategy {
    id: number;
    tradingProfileId: number | null;
    optionBuyingStrategyIds?: number[];
    exitCandidateIds?: number[];
}

export interface PortfolioBacktestFormStrategySetting {
    settingsId: number | null;
    optionBuyingStrategyIds: number[];
    exitCandidateIds: number[];
    positionSizeRules?: PositionSizeRule[];
}

export interface PortfolioBacktestDuplicateFormState {
    replenishmentRules?: BacktestReplenishmentRule[];
    includedStrategyIds: number[];
    missingStrategyIds: number[];
    positionSizeRules: PositionSizeRule[];
    profileChanges: PortfolioBacktestDuplicateProfileChange[];
    strategySettings: Record<number, PortfolioBacktestFormStrategySetting>;
}

export interface PortfolioBacktestDuplicateProfileChange {
    currentTradingProfileId: number;
    originalTradingProfileId: number;
    originalTradingProfileName?: string;
    strategyId: number;
}

const clonePositionSizeRules = (rules: readonly PositionSizeRule[]): PositionSizeRule[] => structuredClone([...rules]);

/**
 * Starts from the portfolio's live strategy profiles, then overlays only the selected run inputs
 * that are intentionally safe to duplicate: OBS, exits, and position sizing.
 */
export const buildPortfolioBacktestDuplicateFormState = (
    sourceBacktest: PortfolioBacktestDuplicateSource,
    liveStrategies: readonly PortfolioBacktestDuplicateLiveStrategy[]
): PortfolioBacktestDuplicateFormState => {
    const strategySettings: Record<number, PortfolioBacktestFormStrategySetting> = {};

    for (const strategy of liveStrategies) {
        strategySettings[strategy.id] = {
            settingsId: strategy.tradingProfileId || null,
            optionBuyingStrategyIds: [...(strategy.optionBuyingStrategyIds ?? [])],
            exitCandidateIds: [...(strategy.exitCandidateIds ?? [])]
        };
    }

    const includedStrategyIds: number[] = [];
    const missingStrategyIds: number[] = [];
    const profileChanges: PortfolioBacktestDuplicateProfileChange[] = [];

    for (const frozenStrategy of sourceBacktest.strategiesSubscribed) {
        const liveSetting = strategySettings[frozenStrategy.id];

        if (!liveSetting) {
            missingStrategyIds.push(frozenStrategy.id);
            continue;
        }

        includedStrategyIds.push(frozenStrategy.id);
        const originalTradingProfileId = frozenStrategy.tradingProfile?.id ?? frozenStrategy.tradingProfileId ?? null;

        if (liveSetting.settingsId !== null && originalTradingProfileId !== null && originalTradingProfileId !== liveSetting.settingsId) {
            profileChanges.push({
                currentTradingProfileId: liveSetting.settingsId,
                originalTradingProfileId,
                ...(frozenStrategy.tradingProfile?.name ? { originalTradingProfileName: frozenStrategy.tradingProfile.name } : {}),
                strategyId: frozenStrategy.id
            });
        }

        strategySettings[frozenStrategy.id] = {
            settingsId: liveSetting.settingsId,
            optionBuyingStrategyIds: [...(frozenStrategy.optionBuyingStrategyIds ?? [])],
            exitCandidateIds: [...(frozenStrategy.exitCandidateIds ?? [])],
            ...(frozenStrategy.portfolioBacktestPositionSizeRules?.length
                ? { positionSizeRules: clonePositionSizeRules(frozenStrategy.portfolioBacktestPositionSizeRules) }
                : {})
        };
    }

    return {
        ...(sourceBacktest.settings.replenishmentRules === undefined
            ? {}
            : { replenishmentRules: structuredClone(sourceBacktest.settings.replenishmentRules) }),
        includedStrategyIds,
        missingStrategyIds,
        positionSizeRules: clonePositionSizeRules(sourceBacktest.settings.positionSizeRules ?? []),
        profileChanges,
        strategySettings
    };
};
